'use client';

/**
 * VideoReelPlayer — sibling of AudioReelPlayer for video demo reels.
 *
 * Same outer frame (border, gradient, padding, rounded corners) so the two
 * read as a pair in the Demo Reels list. The video itself uses HTML5
 * <video controls>. Modern browsers paint the first frame as the poster
 * once metadata loads, so we don't manually canvas-extract a frame for
 * MVP — that's a Phase 2 enhancement if a generic-looking poster reads
 * as a regression.
 */

import * as React from 'react';
import { useState } from 'react';

export interface VideoReelPlayerProps {
  src?: string;
  title: string;
  subtitle?: string;
  /** Optional poster URL; falls back to first-frame paint via preload="metadata". */
  poster?: string;
  allowDownload?: boolean;
  liked?: boolean;
  onLike?: (next: boolean) => void;
  onShare?: () => void;
  onDownload?: () => void;
}

export function VideoReelPlayer({
  src,
  title,
  subtitle,
  poster,
  allowDownload = false,
  liked: likedProp,
  onLike,
  onShare,
  onDownload,
}: VideoReelPlayerProps) {
  const [internalLiked, setInternalLiked] = useState(false);
  const liked = likedProp ?? internalLiked;

  const toggleLike = () => {
    const next = !liked;
    if (likedProp === undefined) setInternalLiked(next);
    onLike?.(next);
  };

  return (
    <div className="rounded-xl border border-border-subtle bg-surface-2 p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-snug text-text-primary">{title}</div>
          {subtitle && <div className="mt-0.5 text-[11.5px] text-text-tertiary">{subtitle}</div>}
        </div>
      </div>

      {src ? (
        <video
          src={src}
          poster={poster}
          controls
          preload="metadata"
          className="w-full max-h-[420px] rounded-lg bg-black"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="aspect-video w-full rounded-lg bg-surface-3 grid place-items-center text-text-tertiary text-sm">
          No preview available
        </div>
      )}

      <div className="flex items-center gap-1.5 border-t border-border-subtle pt-2">
        <IconButton onClick={toggleLike} ariaLabel="Like reel" pressed={liked} className={liked ? 'text-accent' : ''}>
          <HeartIcon filled={liked} />
        </IconButton>
        {allowDownload && (
          <IconButton onClick={onDownload} ariaLabel="Download">
            <DownloadIcon />
          </IconButton>
        )}
        <IconButton onClick={onShare} ariaLabel="Share">
          <ShareIcon />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  onClick,
  ariaLabel,
  pressed,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  ariaLabel: string;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      aria-label={ariaLabel}
      aria-pressed={pressed}
      className={`grid h-9 w-9 place-items-center rounded-md border-0 bg-transparent text-text-tertiary transition hover:bg-surface-3 hover:text-text-primary ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M9 14.5c-3-1.8-6-4.2-6-7.2A3.3 3.3 0 016.3 4 3 3 0 019 5.6 3 3 0 0111.7 4 3.3 3.3 0 0115 7.3c0 3-3 5.4-6 7.2z" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3v9M5 8.5l4 4 4-4M3.5 14.5h11" />
  </svg>
);
const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="9" r="2" />
    <circle cx="13" cy="4.5" r="2" />
    <circle cx="13" cy="13.5" r="2" />
    <path d="M6.7 8l4.6-2.6M6.7 10l4.6 2.6" />
  </svg>
);

export default VideoReelPlayer;
